// The SUBSCRIPTION slice of Frontend-web's utils/currency.js — and nothing else.
//
// This console prints platform money in exactly two places: the lock modal and
// the Plan requests page. So it carries only the four helpers those need. The
// hospital-currency machinery over there (formatPrice, hospitalCurrencyFrom,
// the earnings-row fallbacks) prints a HOSPITAL's money, which a Super Admin
// screen never shows — copying it here would be a second copy to keep in step
// for a reader that does not exist.
//
// WHY A HELPER AT ALL. SubscriptionLockModal used to print a hard-coded `€`
// against `pending.due` — the last euro sign in this app. Plans are sold in
// rupees now, and a sign typed into a file cannot follow the row it labels.

const CODE_RE = /^[A-Za-z]{3}$/;

// Intl's answer, memoised — building a NumberFormat per table row is wasteful.
const signCache = new Map();

const intlSign = (code) => {
    const upper = code.toUpperCase();
    if (signCache.has(upper)) return signCache.get(upper);
    let sign = '';
    try {
        const parts = new Intl.NumberFormat('en', {
            style: 'currency',
            currency: upper,
            currencyDisplay: 'narrowSymbol',
        }).formatToParts(0);
        sign = parts.find((p) => p.type === 'currency')?.value || '';
    } catch {
        // An invalid code ("XX1"), or a runtime without narrowSymbol support.
        sign = '';
    }
    // Intl echoes an unknown-but-well-formed code back ("ABC"); that is not a sign.
    if (sign.toUpperCase() === upper) sign = '';
    signCache.set(upper, sign);
    return sign;
};

/**
 * The sign to print in front of an amount, '' when nothing names a currency.
 *
 * Payment rows send a plain ISO string ('INR'), but the API's other money
 * shapes are objects ({ currencyCode, currencySymbol }), so both are read here
 * exactly as Frontend-web reads them. The CODE decides: a stored currencySymbol
 * is free text somebody typed into a master, while the ISO code is what money
 * is actually charged in.
 */
export const currencySymbol = (currency) => {
    if (currency === null || currency === undefined || currency === '') return '';

    const raw = typeof currency === 'object' ? (currency.currencyCode ?? currency.code) : currency;
    const code = typeof raw === 'string' && CODE_RE.test(raw.trim()) ? raw.trim().toUpperCase() : '';
    if (code) {
        const sign = intlSign(code);
        if (sign) return sign;
    }

    if (typeof currency === 'object') {
        const stored = currency.currencySymbol ?? currency.symbol;
        if (typeof stored === 'string' && stored.trim()) return stored.trim();
        return code;
    }

    return typeof currency === 'string' ? currency.trim() : '';
};

// ---- Subscription prices: the platform's money, not the hospital's ----------
//
// A doctor's plan is sold by the PLATFORM, so its price is not in the hospital's
// currency. What the backend actually stores (round 7, 22 Sep 2026):
//   · a payment row (`subscription_payments.currency`) names its currency — the
//     invoice rows written by the request/activate flow say 'INR';
//   · a PACKAGE row, the status's `pending` block and the admin list's
//     synthesised pending rows carry NO currency field at all. Their price is
//     charged against those same invoices, so it is in the same currency.
// So every subscription screen reads the row's own `currency` first and falls
// back to SUBSCRIPTION_CURRENCY — this one constant, instead of a sign typed
// into a file. When packages gain a currency column, the screens follow the row
// without another edit.
export const SUBSCRIPTION_CURRENCY = 'INR';

/** The currency a subscription row is priced in: its own, else the platform's. */
export const subscriptionCurrency = (row) => {
    const own = row && typeof row === 'object' ? row.currency : null;
    return own && currencySymbol(own) ? own : SUBSCRIPTION_CURRENCY;
};

/** The sign for a subscription row's price. */
export const subscriptionSymbol = (row) => currencySymbol(subscriptionCurrency(row));

/**
 * A subscription price as the plan screens print it — the sign straight against
 * the amount ("₹999", "₹1,999"). The amount is grouped only when asked, because
 * the plan cards show the admin's price exactly as it was typed.
 * @param {number|string} amount
 * @param {object|null} [row]            the package / payment / pending row it belongs to
 * @param {{ grouped?: boolean }} [opts]
 */
export const subscriptionPrice = (amount, row, { grouped = false } = {}) => {
    const shown = grouped ? (Number(amount) || 0).toLocaleString() : String(amount ?? '');
    return `${subscriptionSymbol(row)}${shown}`;
};

export default subscriptionPrice;
